import React from 'react';
import { LANDING_URL, AUTH_URL } from '../lib/service-registry';
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  WifiIcon,
  ClockIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';

interface ErrorViewProps {
  error: Error | string | {
    message: string;
    code?: string;
    statusCode?: number;
    isTimeout?: boolean;
    isNetworkError?: boolean;
  };
  onRetry?: () => void;
  title?: string;
}

/**
 * GCP Material Light error view — matches NoEmployeeRecord / AccessDeniedView card pattern.
 * White card on #f8f9fa background, Inter + Noto Sans KR, #1a73e8 primary CTA.
 */
const ErrorView: React.FC<ErrorViewProps> = ({ error, onRetry, title }) => {
  const errorInfo = typeof error === 'string'
    ? { message: error, code: 'UNKNOWN' }
    : error instanceof Error
    ? { message: error.message, code: 'ERROR' }
    : error;

  const isTimeout = errorInfo.isTimeout ?? false;
  const isNetworkError = errorInfo.isNetworkError ?? false;
  const statusCode = errorInfo.statusCode;
  const is401 = statusCode === 401;

  const ErrorIcon = isTimeout ? ClockIcon : isNetworkError ? WifiIcon : ExclamationTriangleIcon;
  const iconColor = isNetworkError ? '#e37400' : '#d93025';
  const iconBg = isNetworkError ? '#fef7e0' : '#fce8e6';

  const errorTitle = title || (
    is401 ? '세션이 만료되었습니다' :
    isTimeout ? '서버 응답 시간이 초과되었습니다' :
    isNetworkError ? '네트워크 연결 오류' :
    '일시적인 오류가 발생했습니다'
  );

  const helpText = is401
    ? '보안을 위해 세션이 종료되었습니다. 다시 로그인해 주세요.'
    : isTimeout
    ? '서버가 응답하지 않습니다. 잠시 후 다시 시도해 주세요.'
    : isNetworkError
    ? '서버와 연결할 수 없습니다. 네트워크 상태를 확인해 주세요.'
    : '문제가 계속되면 시스템 관리자에게 문의해 주세요.';

  const handleLogin = () => {
    window.location.href = `${AUTH_URL}/api/signout?post_logout=${LANDING_URL}/signed-out`;
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        width: '100%',
        padding: '24px',
      }}
      role="alert"
      aria-live="assertive"
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e8eaed',
          boxShadow: '0 1px 3px rgba(60,64,67,0.12)',
          padding: '32px 28px',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: iconBg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
          }}
          aria-hidden="true"
        >
          <ErrorIcon style={{ width: '24px', height: '24px', color: iconColor }} />
        </div>

        {/* Title */}
        <h3
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#202124',
            margin: '0 0 8px',
            lineHeight: '1.4',
          }}
        >
          {errorTitle}
        </h3>

        {/* Message */}
        <p
          style={{
            fontSize: '13px',
            color: '#5f6368',
            lineHeight: '1.6',
            margin: '0 0 16px',
          }}
        >
          {helpText}
        </p>

        {/* Error code badge */}
        {errorInfo.code && statusCode && (
          <div
            style={{
              display: 'inline-block',
              fontSize: '11px',
              color: '#80868b',
              background: '#f8f9fa',
              padding: '4px 10px',
              borderRadius: '4px',
              marginBottom: '20px',
              fontFamily: 'monospace',
              letterSpacing: '0.025em',
            }}
          >
            {errorInfo.code} ({statusCode})
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
          {is401 ? (
            <button
              onClick={handleLogin}
              style={{
                background: '#1a73e8',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <ArrowRightOnRectangleIcon style={{ width: '18px', height: '18px' }} />
              다시 로그인
            </button>
          ) : onRetry ? (
            <button
              onClick={onRetry}
              style={{
                background: '#1a73e8',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <ArrowPathIcon style={{ width: '18px', height: '18px' }} />
              다시 시도
            </button>
          ) : null}

          {is401 && (
            <a
              href={LANDING_URL}
              style={{
                fontSize: '13px',
                color: '#5f6368',
                textDecoration: 'none',
                paddingTop: '4px',
              }}
            >
              TSCORP 포털로 돌아가기
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorView;
